import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { PageHero, Container, Overline } from "../components/Section";
import { api, formatApiError } from "../lib/api";
import { Heart, ArrowRight, Smartphone, Mail, MapPin, Copy, CreditCard, Clock, ExternalLink } from "lucide-react";

const PACKAGES = [
  { id: "tithe_25", amount: 25 },
  { id: "tithe_50", amount: 50 },
  { id: "tithe_100", amount: 100 },
  { id: "tithe_250", amount: 250 },
  { id: "tithe_500", amount: 500 },
];
const FUNDS = ["General Tithe", "Building Fund", "Missions", "Youth Ministry", "Outreach"];

// External giving URLs — update these once the church provides their account links
const GIVELIFY_URL = "https://www.givelify.com/donate/MjAyNw==/selection";
const CASHAPP_URL = "https://cash.app/$SMZBTGIVE";

export default function Donate() {
  const [packageId, setPackageId] = useState("tithe_100");
  const [customAmount, setCustomAmount] = useState("");
  const [fund, setFund] = useState("General Tithe");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const copyTo = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const startCheckout = async () => {
    setLoading(true);
    try {
      const payload = {
        fund, donor_name: name, donor_email: email || undefined,
        origin_url: window.location.origin,
      };
      const custom = parseFloat(customAmount);
      if (!isNaN(custom) && custom > 0) payload.custom_amount = custom;
      else payload.package_id = packageId;
      const { data } = await api.post("/donations/checkout", payload);
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };

  return (
    <div data-testid="donate-page">
      <PageHero
        overline="Donations · 4 Ways to Give"
        title={<>Generosity that <span className="brand-gradient-text">moves heaven</span>.</>}
        kicker="Your gift fuels worship, discipleship, missions, and community outreach in Hampton and beyond. Thank you for sowing into Kingdom work."
        image="/images/branding/ways-to-give.jpg"
      />

      <Container className="pb-32">
        {/* THREE WAYS TO GIVE */}
        <div className="mb-24">
          <Overline>Choose Your Way</Overline>
          <h2 className="text-4xl md:text-5xl font-sans font-black mb-10 tracking-[-0.02em]">4 Ways to <span className="brand-gradient-text">Give</span>.</h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* GIVELIFY */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="glass p-10 group"
              data-testid="give-givelify"
            >
              <div className="text-7xl font-sans font-black brand-gradient-text mb-6 opacity-90">01</div>
              <div className="flex items-center gap-3 mb-3">
                <div className="px-4 py-1.5 rounded-full bg-white text-royal text-xs font-extrabold tracking-wider">GIVELIFY</div>
              </div>
              <h3 className="font-sans text-2xl font-extrabold mb-2 brand-gradient-text-warm">Give via Givelify</h3>
              <p className="text-white/70 text-sm leading-relaxed mb-6">Secure online & mobile giving via our verified Givelify church account. Card, ACH, Apple Pay, Google Pay.</p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={GIVELIFY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-wine px-5 py-2.5 rounded-full text-xs uppercase tracking-widest font-bold inline-flex items-center gap-2"
                  data-testid="givelify-open-btn"
                >
                  Open Givelify <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </motion.div>

            {/* CASH APP */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="glass p-10 group"
              data-testid="give-cashapp"
            >
              <div className="text-7xl font-sans font-black brand-gradient-text mb-6 opacity-90">02</div>
              <div className="flex items-center gap-3 mb-3">
                <div className="px-4 py-1.5 rounded-full bg-[#00D632] text-black text-xs font-extrabold tracking-wider inline-flex items-center gap-1"><Smartphone className="h-3 w-3" /> CASH APP</div>
              </div>
              <h3 className="font-sans text-2xl font-extrabold mb-2 brand-gradient-text-warm">Give via Cash App</h3>
              <p className="text-white/70 text-sm leading-relaxed mb-6">Open Cash App and complete your gift in seconds — the link takes you straight to our giving page.</p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={CASHAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-wine px-5 py-2.5 rounded-full text-xs uppercase tracking-widest font-bold inline-flex items-center gap-2"
                  data-testid="cashapp-open-btn"
                >
                  Open Cash App <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </motion.div>

            {/* MAIL IN */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="glass p-10 group"
              data-testid="give-mail"
            >
              <div className="text-7xl font-sans font-black brand-gradient-text mb-6 opacity-90">03</div>
              <div className="flex items-center gap-3 mb-3">
                <div className="px-4 py-1.5 rounded-full bg-white text-royal text-xs font-extrabold tracking-wider inline-flex items-center gap-1"><Mail className="h-3 w-3" /> MAIL IN</div>
              </div>
              <h3 className="font-sans text-xl font-extrabold mb-2 leading-snug">3100 Butternut Drive<br/>Hampton, VA 23666</h3>
              <div className="flex items-center gap-2 text-sm text-white/70 mt-3 mb-6">
                <Clock className="h-4 w-4 text-gold" /> Tue–Fri · 10AM – 5PM
              </div>
              <button
                onClick={() => copyTo("3100 Butternut Drive, Hampton, VA 23666", "Mailing address")}
                className="btn-ghost px-5 py-2.5 rounded-full text-xs uppercase tracking-widest font-bold inline-flex items-center gap-2"
                data-testid="mail-copy-btn"
              >
                <Copy className="h-3 w-3" /> Copy Address
              </button>
            </motion.div>

            {/* ONLINE CARD GIVING */}
            <motion.a
              href="#card-giving"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="glass p-10 hover:bg-white/[0.08] transition-colors group block"
              data-testid="give-card"
            >
              <div className="text-7xl font-sans font-black brand-gradient-text mb-6 opacity-90">04</div>
              <div className="flex items-center gap-3 mb-3">
                <div className="px-4 py-1.5 rounded-full bg-white text-royal text-xs font-extrabold tracking-wider inline-flex items-center gap-1"><CreditCard className="h-3 w-3" /> ON THIS SITE</div>
              </div>
              <h3 className="font-sans text-2xl font-extrabold mb-2 brand-gradient-text-warm">Card / ACH</h3>
              <p className="text-white/70 text-sm leading-relaxed mb-6">Give instantly with debit, credit, or ACH via our secure Stripe checkout — right here on smzbt.org.</p>
              <div className="inline-flex items-center gap-2 text-ember group-hover:text-gold transition-colors text-sm uppercase tracking-widest font-bold">
                Give on Site <ArrowRight className="h-4 w-4" />
              </div>
            </motion.a>
          </div>
        </div>

        <div className="divider-wine my-16" />

        {/* CARD GIVING (optional fast-path) */}
        <div id="card-giving" className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <Overline>Card Giving · Powered by Stripe</Overline>
            <h2 className="font-sans text-3xl md:text-4xl font-black mb-2 tracking-[-0.02em]">Or give instantly by card.</h2>
            <p className="text-white/65 mb-10">For convenience, you can also give one-time directly on this site via our secure Stripe checkout.</p>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
              {PACKAGES.map((p) => (
                <button
                  key={p.id}
                  onClick={() => { setPackageId(p.id); setCustomAmount(""); }}
                  className={`py-6 px-4 border transition-all ${
                    packageId === p.id && !customAmount
                      ? "border-gold bg-gold/10 text-gold"
                      : "border-white/20 hover:border-white/50 text-white/85"
                  }`}
                  data-testid={`amount-${p.amount}`}
                >
                  <div className="font-sans text-3xl font-black">${p.amount}</div>
                </button>
              ))}
            </div>

            <div className="mb-8">
              <div className="text-xs uppercase tracking-widest text-white/55 mb-2 font-bold">Or custom amount</div>
              <div className="relative">
                <span className="absolute left-0 top-3 text-2xl font-sans font-black text-gold">$</span>
                <input
                  type="number"
                  min="1"
                  step="any"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="w-full bg-transparent border-0 border-b border-white/20 text-3xl font-sans font-extrabold pl-8 py-3 focus:outline-none focus:border-gold transition-colors"
                  placeholder="0.00"
                  data-testid="custom-amount-input"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
              <Field label="Designation">
                <select value={fund} onChange={(e) => setFund(e.target.value)} className={inputCls} data-testid="fund-select">
                  {FUNDS.map((f) => <option key={f} value={f} className="bg-royal">{f}</option>)}
                </select>
              </Field>
              <Field label="Your Name (optional)">
                <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="donor-name-input" />
              </Field>
              <Field label="Email (for receipt)" className="sm:col-span-2">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="donor-email-input" />
              </Field>
            </div>

            <button
              disabled={loading}
              onClick={startCheckout}
              className="btn-wine w-full sm:w-auto px-10 py-4 rounded-full text-sm uppercase tracking-[0.2em] font-bold inline-flex items-center gap-3 mt-10 disabled:opacity-50"
              data-testid="donate-checkout-btn"
            >
              <CreditCard className="h-4 w-4" /> {loading ? "Redirecting…" : "Continue to Secure Checkout"} <ArrowRight className="h-4 w-4" />
            </button>
            <p className="text-xs text-white/40 mt-4">Powered by Stripe · Your information is encrypted and secure.</p>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-5">
            <div className="glass p-10 sticky top-28">
              <Overline>Where it Goes</Overline>
              <h3 className="font-sans text-3xl font-black mb-6 tracking-[-0.02em]">Every dollar, <span className="brand-gradient-text-warm">deployed.</span></h3>
              <ul className="space-y-5 text-white/85">
                {[
                  { pct: "40%", label: "Ministries & Worship" },
                  { pct: "25%", label: "Missions & Outreach" },
                  { pct: "20%", label: "Facilities & Operations" },
                  { pct: "15%", label: "Benevolence & Community" },
                ].map((it) => (
                  <li key={it.label} className="flex items-baseline justify-between gap-4 pb-4 border-b border-white/8 last:border-0">
                    <span className="text-sm">{it.label}</span>
                    <span className="font-sans font-black text-gold text-2xl">{it.pct}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-white/60 mt-8 italic">"Each of you should give what you have decided in your heart to give, not reluctantly or under compulsion, for God loves a cheerful giver." — 2 Cor 9:7</p>
            </div>
          </aside>
        </div>

        {/* OFFICE INFO */}
        <div className="mt-24 grid md:grid-cols-2 gap-px bg-white/5">
          <div className="bg-royal/40 p-10">
            <Overline>Mailing Address</Overline>
            <div className="flex items-start gap-4 mt-4">
              <MapPin className="h-6 w-6 text-gold shrink-0" />
              <div>
                <div className="text-2xl font-sans font-extrabold">Sixth Mount Zion Baptist Temple</div>
                <div className="text-white/80 mt-1">3100 Butternut Drive<br/>Hampton, VA 23666</div>
              </div>
            </div>
          </div>
          <div className="bg-royal/40 p-10">
            <Overline>Office Hours</Overline>
            <div className="flex items-start gap-4 mt-4">
              <Clock className="h-6 w-6 text-gold shrink-0" />
              <div>
                <div className="text-2xl font-sans font-extrabold">Tuesday – Friday</div>
                <div className="text-white/80 mt-1">10:00 AM – 5:00 PM</div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}

const inputCls = "w-full bg-transparent border-0 border-b border-white/20 text-white px-0 py-3 focus:outline-none focus:border-gold transition-colors";
function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs uppercase tracking-widest text-white/55 mb-2 font-bold">{label}</div>
      {children}
    </label>
  );
}
